import React, { Component, createContext, useContext } from 'react'
import createAuth0Client from '@auth0/auth0-spa-js'
import callSecureAPI from '../API/callSecureAPI'
import endpoints from '../API/endpoints'
import axios from 'axios'
import store from '../redux/store'
import { resetAdminSplashStatus, setRecentlyLoggedIn } from '../utilities/adminSplashScreen'

// create the context
export const Auth0Context = createContext()

// export the context as useAuth0
export const useAuth0 = () => useContext(Auth0Context) // <-- new

// create a provider
export class Auth0Provider extends Component {
    state = {
        auth0Client: null,
        isLoading: true,
        isAuthenticated: false,
        user: null,
        error: null,
    }
    config = {
        domain: process.env.REACT_APP_AUTH0_DOMAIN,
        client_id: process.env.REACT_APP_AUTH0_CLIENT_ID,
        redirect_uri: window.location.origin,
        useRefreshTokens: true,
        audience: 'https://atlas.aesara.com',
        cacheLocation: 'memory',
    }

    componentDidMount() {
        this.initializeAuth0()
    }

    initializeAuth0 = async () => {
        const hadAuthCallback = window.location.search.includes('code=') && window.location.search.includes('state=')
        // Save the full URL before createAuth0Client, because other components
        // may render during the async call and strip the query params
        const callbackUrl = hadAuthCallback ? window.location.href : null

        try {
            console.log(
                'Auth0: Creating client with domain:',
                this.config.domain,
                'redirect_uri:',
                this.config.redirect_uri
            )
            const auth0Client = await createAuth0Client(this.config)
            console.log('Auth0: Client created successfully')
            this.setState({ auth0Client })

            // Check if we're on a callback URL after login
            if (hadAuthCallback) {
                resetAdminSplashStatus()
                setRecentlyLoggedIn()
                try {
                    // Handle the redirect FIRST before clearing URL
                    const result = await auth0Client.handleRedirectCallback(callbackUrl)
                    console.log('Auth0: Successfully handled redirect callback', result)

                    // AFTER successfully handling redirect, clear the URL
                    window.history.replaceState({}, document.title, window.location.pathname)

                    // If user is authenticated, get token and fetch CSRF token
                    if (await auth0Client.isAuthenticated()) {
                        const token = await auth0Client.getTokenSilently({
                            audience: 'https://atlas.aesara.com',
                        })

                        // Fetch CSRF token
                        try {
                            const response = await axios.get('/csrf-token', {
                                withCredentials: true,
                                headers: {
                                    Authorization: `Bearer ${token}`,
                                },
                            })
                            console.log('CSRF token acquired')

                            // Log LOGIN event
                            try {
                                await axios.post(
                                    '/api/event/log',
                                    { action: 'LOGIN' },
                                    {
                                        withCredentials: true,
                                        headers: {
                                            Authorization: `Bearer ${token}`,
                                            'x-csrf-token': response.data?.csrfToken,
                                        },
                                    }
                                )
                                console.log('Login event logged')
                            } catch (loginLogError) {
                                console.error('Error logging login event:', loginLogError)
                            }
                        } catch (csrfError) {
                            console.error('Error getting CSRF token:', csrfError)
                        }
                    }
                } catch (error) {
                    console.error('Auth0: Error handling redirect callback:', error)
                    // Still clear the URL even if there was an error
                    window.history.replaceState({}, document.title, window.location.pathname)
                }
            }

            // Now check authentication status
            const isAuthenticated = await auth0Client.isAuthenticated()
            console.log('Auth0: isAuthenticated:', isAuthenticated)

            // If we just came back from Auth0 but auth failed, don't allow another redirect
            if (hadAuthCallback && !isAuthenticated) {
                console.error('Auth0: Callback received but authentication failed. Stopping to prevent redirect loop.')
                this.setState({
                    isLoading: false,
                    isAuthenticated: false,
                    user: null,
                    error: 'Authentication failed after redirect. The Auth0 callback was received but the session could not be established. Please check Auth0 configuration (Allowed Callback URLs, Allowed Web Origins) or contact support.',
                })
                return
            }

            let user = null
            if (isAuthenticated) {
                user = await auth0Client.getUser()
                console.log('Auth0: User:', user?.email)

                // Get CSRF token if we didn't already get it during redirect callback
                if (!hadAuthCallback) {
                    try {
                        const token = await auth0Client.getTokenSilently({
                            audience: 'https://atlas.aesara.com',
                        })

                        const response = await axios.get('/csrf-token', {
                            withCredentials: true,
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                        })
                        console.log('CSRF token acquired')
                    } catch (csrfError) {
                        console.error('Error getting CSRF token:', csrfError)
                    }
                }
            }

            this.setState({
                isLoading: false,
                isAuthenticated,
                user,
            })
        } catch (error) {
            console.error('Auth0: Initialization error:', error)
            this.setState({
                isLoading: false,
                isAuthenticated: false,
                user: null,
                error: hadAuthCallback
                    ? 'Authentication failed during initialization. Please check Auth0 configuration or contact support.'
                    : null,
            })
        }
    }

    render() {
        const { auth0Client, isLoading, isAuthenticated, user, error } = this.state
        const { children } = this.props

        const configObject = {
            isLoading,
            isAuthenticated,
            user,
            error,
            loginWithRedirect: (...p) => auth0Client?.loginWithRedirect(...p),
            getTokenSilently: (...p) => auth0Client?.getTokenSilently(...p),
            getIdTokenClaims: (...p) => auth0Client?.getIdTokenClaims(...p),
            logout: (...p) => {
                resetAdminSplashStatus()
                if (auth0Client) {
                    return auth0Client.logout(...p)
                }
                // Fallback: manually redirect to Auth0 logout
                const returnTo = p[0]?.returnTo || window.location.origin
                window.location.assign(
                    `https://${process.env.REACT_APP_AUTH0_DOMAIN}/v2/logout?client_id=${process.env.REACT_APP_AUTH0_CLIENT_ID}&returnTo=${encodeURIComponent(returnTo)}`
                )
            },
        }

        return <Auth0Context.Provider value={configObject}>{children}</Auth0Context.Provider>
    }
}
